import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Row, Col, DatePicker, Select, Form, Typography } from "antd";
import MainHeader, { MENU_REPORT } from "../../components/common/MainHeader";
import { WOOD_IMPORT_GRADE_DISPLAY } from "../../constance/dataTypes";
import ReportDetail from "../../components/reports/ReportDetail";
import { URL_BARCODE } from "../../constance/urls";
import { formatComma } from "../../utils/stringFormat";
import ReportExportButton from "../../components/common/ReportExportButton";
import { DATE_FORMAT, HUMAN_DATE_FORMAT } from "../../constance/strings";
import dayjs from "dayjs";

export default function ReportWoodImportByCompany() {
  const { t } = useTranslation();
  const [queryParams, setQueryParams] = useState(null);
  const [form] = Form.useForm();
  const date = Form.useWatch("dateRange", form);
  const [errors, setErrors] = useState({});

  const ref = useRef();

  const grade = Form.useWatch("wood_grade_options", form);

const columns = React.useMemo(
  () => [
    {
      title: t("truck_companies.code"),
      dataIndex: "truck_company_code",
    },
    {
      title: t("truck_companies.name"),
      dataIndex: "truck_company_name",
    },
    {
      title: t("wood_import_customer.sent_qty"),
      dataIndex: "report.total_woods",
      render: (text, record) => formatComma(record.report?.total_woods, 0),
    },
    {
      title: t("wood_import_customer.good_wood_qty"),
      dataIndex: "report.good_woods",
      render: (text, record) => formatComma(record.report?.good_woods, 0),
    },
    {
      title: t("wood_import_customer.volume_cuft"),
      dataIndex: "report.good_volume",
      render: (text, record) => formatComma(record.report?.good_volume, 4),
    },
    {
      title: t("wood_import_customer.waste_wood_qty"),
      dataIndex: "report.waste_woods",
      render: (text, record) => formatComma(record.report?.waste_woods, 0),
    },
    {
      title: t("wood_import_customer.volume_cuft"),
      dataIndex: "report.waste_volume",
      render: (text, record) => formatComma(record.report?.waste_volume, 4),
    },
    {
      title: t("wood_import_customer.total_volume_cuft"),
      dataIndex: "report.total_volume",
      render: (text, record) => formatComma(record.report?.total_volume, 4),
    },
    {
      title: t("wood_import_customer.purchase_amount"),
      dataIndex: "report.buy_amount", 
      render: (text, record) => formatComma(record.report?.buy_amount),
    },
    {
      title: t("wood_import_customer.sales_amount"),
      dataIndex: "report.sell_amount",
      render: (text, record) => formatComma(record.report?.sell_amount),
    },
    {
      title: t("wood_import_customer.profit_diff"),
      dataIndex: "report.diff_amount",
      render: (text, record) => formatComma(record.report?.diff_amount),
    },
  ],
  [t]
);


  const wood_grade_options = WOOD_IMPORT_GRADE_DISPLAY.map((grade) => ({
    value: grade,
    label: grade.toLocaleUpperCase(),
  }));

  const getQueryParams = () => {
    const values = form.getFieldsValue();
    const startDate = dayjs(values.dateRange[0]).format(DATE_FORMAT);
    const endDate = dayjs(values.dateRange[1]).format(DATE_FORMAT);
    const queryString = `date_after=${startDate}&date_before=${endDate}&grade=${grade}`;
    return queryString;
  };

  const validateForm = () => {
    const values = form.getFieldsValue();
    const newErrors = {};
    if (!values.dateRange || values.dateRange.length < 2)
      newErrors.dateRange = t("error_messages.validation.required");
    if (!grade) newErrors.grade = t("error_messages.validation.required");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  React.useEffect(() => {
    if (validateForm()) {
      if (ref.current) {
        const params = getQueryParams();
        ref.current.refetch(params);
        setQueryParams(params);
      }
    }
  }, [date, grade]);

  return (
    <div style={{ margin: "24px", marginTop: "7em" }}>
      <MainHeader activeItem={MENU_REPORT} />
      <Typography.Title level={2}>
        {t("menus.report_menus.wood_import_menus.wood_import_by_customer")}
      </Typography.Title>

      <Form
        layout="horizontal"
        form={form}
        initialValues={{
          wood_grade_options: "ab",
          dateRange: [],
        }}
      >
        <Row gutter={16}>
          <Col sm={24} md={24} lg={6}>
            <Form.Item
              label={t("filter")}
              name="dateRange"
              rules={[{ required: true, message: t("กรุณาเลือกช่วงเวลา") }]}
            >
              <DatePicker.RangePicker
                style={{ width: "100%" }}
                placeholder={[
                  t("searchs.placeholders.startDate"),
                  t("searchs.placeholders.endDate"),
                ]}
                format={HUMAN_DATE_FORMAT}
              />
            </Form.Item>
          </Col>
          <Col sm={24} md={24} lg={6}>
            <Form.Item
              label={t("wood_imports.grade")}
              name="wood_grade_options"
            >
              <Select options={wood_grade_options} />
            </Form.Item>
          </Col>
        </Row>
        <div style={{ textAlign: "right" }}>
          {["pdf", "excel"].map((type) => (
            <ReportExportButton
              key={type}
              url={`${URL_BARCODE.REPORT_WOOD_IMPORT_CUSTOMER_EXPORT}${type}/?${queryParams}`}
              type={type}
              title={`${t(
                "menus.report_menus.wood_import_menus.wood_import_by_customer"
              )}.....`}
              disabled={queryParams == null}
            />
          ))}
        </div>
      </Form>

      <ReportDetail
        ref={ref}
        reportUrl={URL_BARCODE.REPORT_WOOD_IMPORT_CUSTOMER}
        columns={columns}
      />
    </div>
  );
}
